import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAdminFeeConfig, useUpdateAdminFeeConfig } from '@/hooks/api/useUserSettings';
import { LoadingSpinner } from '@/components/ui';

const adminFeeSchema = z.object({
  base_processing_fee: z
    .number({ message: 'Enter a valid amount' })
    .min(0, 'Must be $0 or more')
    .max(10000, 'Must be $10,000 or less'),
  per_inspection_fee: z
    .number({ message: 'Enter a valid amount' })
    .min(0, 'Must be $0 or more')
    .max(10000, 'Must be $10,000 or less'),
  include_permit_buddy_cost: z.boolean(),
});

type AdminFeeFormData = z.infer<typeof adminFeeSchema>;

interface AdminFeeSettingsProps {
  className?: string;
}

export const AdminFeeSettings: React.FC<AdminFeeSettingsProps> = ({ className = '' }) => {
  const { data: config, isLoading } = useAdminFeeConfig();
  const updateConfig = useUpdateAdminFeeConfig();
  const [successMessage, setSuccessMessage] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<AdminFeeFormData>({
    resolver: zodResolver(adminFeeSchema),
    defaultValues: {
      base_processing_fee: 0,
      per_inspection_fee: 0,
      include_permit_buddy_cost: true,
    },
  });

  // Populate form once config loads
  useEffect(() => {
    if (config) {
      reset({
        base_processing_fee: Number(config.base_processing_fee),
        per_inspection_fee: Number(config.per_inspection_fee),
        include_permit_buddy_cost: config.include_permit_buddy_cost,
      });
    }
  }, [config, reset]);

  const baseProcessingFee = watch('base_processing_fee') || 0;
  const perInspectionFee = watch('per_inspection_fee') || 0;
  const includePBCost = watch('include_permit_buddy_cost');

  // Live preview: 3-inspection example with 1 credit ($1.00) Permit Buddy cost
  const examplePBCost = includePBCost ? 1.0 : 0;
  const exampleInspections = 3;
  const exampleTotal =
    examplePBCost +
    Number(baseProcessingFee) +
    Number(perInspectionFee) * exampleInspections;

  const onSubmit = async (data: AdminFeeFormData) => {
    try {
      await updateConfig.mutateAsync(data);
      setSuccessMessage('Admin fee settings saved');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save settings. Please try again.';
      setError('root', { type: 'manual', message });
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Admin Fee Settings</h2>
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="mb-5">
        <h2 className="text-xl font-semibold text-gray-900">Admin Fee Settings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Set the overhead costs to add to permit fee quotes when billing your clients. These are
          your internal costs — they appear as "Admin Overhead" in every fee calculation.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Permit Buddy cost toggle */}
        <div className="flex items-start gap-3">
          <input
            id="include_permit_buddy_cost"
            type="checkbox"
            {...register('include_permit_buddy_cost')}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <div>
            <label
              htmlFor="include_permit_buddy_cost"
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              Include Permit Buddy search cost
            </label>
            <p className="text-xs text-gray-500 mt-0.5">
              Adds the credit cost of each search (e.g. $1.00 for a high-confidence result) so you
              can recover your Permit Buddy subscription costs.
            </p>
          </div>
        </div>

        {/* Base processing fee */}
        <div>
          <label
            htmlFor="base_processing_fee"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Base processing fee ($ per permit)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
              $
            </span>
            <input
              id="base_processing_fee"
              type="number"
              step="0.01"
              min="0"
              max="10000"
              {...register('base_processing_fee', { valueAsNumber: true })}
              className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              placeholder="0.00"
            />
          </div>
          {errors.base_processing_fee && (
            <p className="text-red-600 text-xs mt-1">{errors.base_processing_fee.message}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Your internal labor cost to administer a permit (e.g. paperwork, filing, follow-up).
          </p>
        </div>

        {/* Per-inspection fee */}
        <div>
          <label
            htmlFor="per_inspection_fee"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Per-inspection coordination fee ($ per inspection)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
              $
            </span>
            <input
              id="per_inspection_fee"
              type="number"
              step="0.01"
              min="0"
              max="10000"
              {...register('per_inspection_fee', { valueAsNumber: true })}
              className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              placeholder="0.00"
            />
          </div>
          {errors.per_inspection_fee && (
            <p className="text-red-600 text-xs mt-1">{errors.per_inspection_fee.message}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Cost to schedule and coordinate each inspection trip. Multiplied by the number of
            inspections you enter on each search.
          </p>
        </div>

        {/* Live preview */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4 text-sm">
          <p className="font-medium text-indigo-900 mb-2">
            Example: 3-inspection job (high-confidence search)
          </p>
          <div className="space-y-1 text-indigo-800 font-mono text-xs">
            {includePBCost && (
              <div className="flex justify-between">
                <span>Permit Buddy cost</span>
                <span>${examplePBCost.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Base processing fee</span>
              <span>${Number(baseProcessingFee).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Inspections ({exampleInspections} × ${Number(perInspectionFee).toFixed(2)})</span>
              <span>${(Number(perInspectionFee) * exampleInspections).toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-indigo-300 pt-1 font-semibold">
              <span>Total admin overhead</span>
              <span>${exampleTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Feedback */}
        {errors.root && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-red-700 text-sm">{errors.root.message}</p>
          </div>
        )}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3">
            <p className="text-green-700 text-sm">{successMessage}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition-colors"
        >
          {isSubmitting ? 'Saving...' : 'Save Admin Fee Settings'}
        </button>
      </form>
    </div>
  );
};
